<script>
    import { createEventDispatcher, tick } from "svelte";
    import { getWeekFromDate } from "../views/supplyPlan/dateWeekUtil.js";

    /**
     * 날짜 입력 + 월요일 시작 달력 (월 화 수 목 금 토 일)
     * - 직접 입력: 브라우저 기본 날짜 입력처럼 연(4자리) / 월(2자리) / 일(2자리) 구간별로 선택되어 바로 덮어쓰기
     *   (구간을 클릭하면 그 구간만 선택, 숫자를 채우면 다음 구간으로 이동, ↑↓ 증감, ←→/Tab 구간 이동)
     *   한 구간이 완성되고 전체가 유효한 날짜일 때만 반영
     * - 달력: 오른쪽 아이콘으로 열림 (브라우저 기본 <input type="date"> 달력은 시작 요일을 바꿀 수 없어 직접 구현)
     * - 값은 'YYYY-MM-DD' 문자열, 변경 시 change 이벤트의 detail 로 전달
     */
    export let value = "";
    export let min = "";
    export let max = "";
    export let compact = false;

    const dispatch = createEventDispatcher();
    const WEEKDAYS = ["월", "화", "수", "목", "금", "토", "일"];
    const POPUP_WIDTH = 312;
    const POPUP_HEIGHT = 340;
    const MIN_YEAR = 2000;
    const MAX_YEAR = 2100;

    let open = false;
    let view = "day"; // 'day' | 'month'
    let viewYear = new Date().getFullYear();
    let viewMonth = new Date().getMonth(); // 0~11
    let root;
    let fieldEl;
    let inputEl;
    let popStyle = "";
    const SEGMENTS = [
        { key: "y", len: 4, start: 0, end: 4, ph: "yyyy" },
        { key: "m", len: 2, start: 5, end: 7, ph: "mm" },
        { key: "d", len: 2, start: 8, end: 10, ph: "dd" },
    ];

    let parts = { y: "", m: "", d: "" };
    let seg = 0; // 선택된 구간 (0: 연, 1: 월, 2: 일)
    let fresh = true; // 구간에 막 들어옴 → 다음 숫자가 기존 값을 덮어씀
    let dirty = false; // 입력 중 바뀐 값이 있음
    let mouseFocus = false;
    let lastDispatched = "";
    let invalid = false;

    // 부모 값이 바뀌면(달력 선택, 프리셋 버튼 등) 입력칸도 맞춤
    $: syncFromValue(value);
    function syncFromValue(v) {
        const p = parse(v);
        parts = p ? { y: String(p.y), m: pad(p.m), d: pad(p.d) } : { y: "", m: "", d: "" };
        dirty = false;
        invalid = false;
    }

    $: text = SEGMENTS.map((s) => (parts[s.key] === "" ? s.ph : parts[s.key].padStart(s.len, "0"))).join("-");

    const pad = (n) => String(n).padStart(2, "0");
    const toStr = (y, m, d) => `${y}-${pad(m)}-${pad(d)}`;

    function parse(s) {
        const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || "");
        return m ? { y: +m[1], m: +m[2], d: +m[3] } : null;
    }

    function todayStr() {
        const t = new Date();
        return toStr(t.getFullYear(), t.getMonth() + 1, t.getDate());
    }

    function isOutOfRange(str) {
        return (min && str < min) || (max && str > max);
    }

    // 실제 존재하는 날짜인지 (2월 30일 등 제외) + 연도 범위
    function isValidDate(str) {
        const p = parse(str);
        if (!p || p.y < MIN_YEAR || p.y > MAX_YEAR) return false;
        const dt = new Date(p.y, p.m - 1, p.d);
        return dt.getFullYear() === p.y && dt.getMonth() === p.m - 1 && dt.getDate() === p.d;
    }

    // ===== 직접 입력 (연 / 월 / 일 구간별) =====
    function segAt(pos) {
        return pos <= 4 ? 0 : pos <= 7 ? 1 : 2;
    }

    async function applySel() {
        await tick();
        if (inputEl && document.activeElement === inputEl) {
            inputEl.setSelectionRange(SEGMENTS[seg].start, SEGMENTS[seg].end);
        }
    }

    // 현재 입력값을 'YYYY-MM-DD' 로 (구간이 비었거나 연도가 4자리가 아니면 '')
    function currentStr() {
        const { y, m, d } = parts;
        if (y.length !== 4 || m === "" || d === "") return "";
        return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
    }

    // 전체가 유효한 날짜일 때만 부모에 반영, 완성됐지만 잘못된 날짜면 빨간 표시
    function tryCommit() {
        const str = currentStr();
        if (!str) return false;
        if (!isValidDate(str) || isOutOfRange(str)) {
            invalid = true;
            return false;
        }
        invalid = false;
        if (str !== value) {
            lastDispatched = str;
            dispatch("change", str);
        }
        return true;
    }

    // 한 자리만 입력된 월/일 등을 0 채움 (예: 월 "1" → "01")
    function padSegment(i) {
        const s = SEGMENTS[i];
        if (parts[s.key] !== "") parts[s.key] = parts[s.key].padStart(s.len, "0");
    }

    function moveSeg(delta) {
        padSegment(seg);
        if (dirty) tryCommit();
        seg = Math.max(0, Math.min(2, seg + delta));
        fresh = true;
        parts = parts;
        applySel();
    }

    function typeDigit(ch) {
        const s = SEGMENTS[seg];
        let buf = (fresh ? "" : parts[s.key]) + ch;
        let done = buf.length >= s.len;
        // 월은 2~9, 일은 4~9 로 시작하면 뒤에 숫자가 올 수 없으므로 바로 확정
        if (s.key === "m" && buf.length === 1 && +buf > 1) {
            buf = "0" + buf;
            done = true;
        }
        if (s.key === "d" && buf.length === 1 && +buf > 3) {
            buf = "0" + buf;
            done = true;
        }
        parts[s.key] = buf;
        parts = parts;
        fresh = false;
        dirty = true;
        invalid = false;
        if (done) {
            tryCommit();
            if (seg < 2) {
                seg += 1;
                fresh = true;
            }
        }
        applySel();
    }

    function clearSegment() {
        parts[SEGMENTS[seg].key] = "";
        parts = parts;
        fresh = true;
        dirty = true;
        invalid = false;
        applySel();
    }

    function daysInMonth(y, m) {
        return new Date(y, m, 0).getDate();
    }

    // ↑↓ : 선택된 구간 값 증감 (비어 있으면 오늘 날짜 기준)
    function step(dir) {
        const t = new Date();
        const y = parts.y.length === 4 ? +parts.y : t.getFullYear();
        const m = parts.m !== "" ? +parts.m : t.getMonth() + 1;
        const d = parts.d !== "" ? +parts.d : t.getDate();
        let ny = y;
        let nm = m;
        let nd = d;
        if (seg === 0) ny = Math.max(MIN_YEAR, Math.min(MAX_YEAR, y + dir));
        if (seg === 1) nm = ((m - 1 + dir + 12) % 12) + 1;
        if (seg === 2) nd = ((d - 1 + dir + daysInMonth(ny, nm)) % daysInMonth(ny, nm)) + 1;
        nd = Math.min(nd, daysInMonth(ny, nm));
        parts = { y: String(ny), m: pad(nm), d: pad(nd) };
        fresh = true;
        dirty = true;
        tryCommit();
        applySel();
    }

    function onBeforeInput(e) {
        const type = e.inputType || "";
        if (type === "insertText" && /^\d$/.test(e.data || "")) {
            e.preventDefault();
            typeDigit(e.data);
        } else if (type === "deleteContentBackward" || type === "deleteContentForward" || type === "deleteByCut") {
            e.preventDefault();
            clearSegment();
        } else if (type !== "insertFromPaste") {
            e.preventDefault(); // 숫자 외 문자 입력 차단
        }
    }

    function onPaste(e) {
        e.preventDefault();
        const digits = ((e.clipboardData && e.clipboardData.getData("text")) || "").replace(/\D/g, "");
        if (digits.length < 8) return;
        parts = { y: digits.slice(0, 4), m: digits.slice(4, 6), d: digits.slice(6, 8) };
        dirty = true;
        tryCommit();
        applySel();
    }

    function onKeydown(e) {
        switch (e.key) {
            case "ArrowLeft":
                e.preventDefault();
                moveSeg(-1);
                break;
            case "ArrowRight":
            case "-":
            case ".":
            case "/":
                e.preventDefault();
                moveSeg(1);
                break;
            case "ArrowUp":
                e.preventDefault();
                step(1);
                break;
            case "ArrowDown":
                e.preventDefault();
                step(-1);
                break;
            case "Tab":
                // 구간이 남아 있으면 구간 이동, 마지막(일)/처음(연)에서는 기본 포커스 이동
                if (!e.shiftKey && seg < 2) {
                    e.preventDefault();
                    moveSeg(1);
                } else if (e.shiftKey && seg > 0) {
                    e.preventDefault();
                    moveSeg(-1);
                }
                break;
            case "Enter":
                e.preventDefault();
                padSegment(seg);
                tryCommit();
                inputEl.blur();
                break;
            case "Escape":
                syncFromValue(value);
                inputEl.blur();
                break;
        }
    }

    function onFocus() {
        if (mouseFocus) return; // 마우스로 들어온 경우 click 에서 클릭한 구간을 선택
        seg = 0;
        fresh = true;
        applySel();
    }

    function onClick() {
        mouseFocus = false;
        seg = segAt(inputEl.selectionStart ?? 0);
        fresh = true;
        applySel();
    }

    function onBlur() {
        // 입력을 끝내고 나갈 때 완성되지 않았거나 잘못된 값이 남아 있으면 적용된 값으로 되돌림
        mouseFocus = false;
        if (dirty) {
            padSegment(seg);
            tryCommit();
        }
        const cur = currentStr();
        if (cur !== value && cur !== lastDispatched) syncFromValue(value);
        dirty = false;
        invalid = false;
    }

    // ===== 달력 =====
    // 현재 보는 달의 6주(42칸) 격자, 월요일 시작
    function buildCells(y, m, selected) {
        const offset = (new Date(y, m, 1).getDay() + 6) % 7; // 월=0 ... 일=6
        const today = todayStr();
        const cells = [];
        for (let i = 0; i < 42; i++) {
            const dt = new Date(y, m, 1 - offset + i);
            const str = toStr(dt.getFullYear(), dt.getMonth() + 1, dt.getDate());
            cells.push({
                str,
                day: dt.getDate(),
                inMonth: dt.getMonth() === m,
                disabled: isOutOfRange(str),
                isToday: str === today,
                isSelected: str === selected,
            });
        }
        return cells;
    }

    // 6주 × 7일 행 구성 + 각 행의 주차(ISO 8601, 월요일 기준: 그리드가 월요일 시작이라 행의 첫 칸이 월요일)
    function buildWeeks(list) {
        const rows = [];
        for (let i = 0; i < list.length; i += 7) {
            const days = list.slice(i, i + 7);
            const w = getWeekFromDate(days[0].str);
            rows.push({ week: w ? w.week : "", days });
        }
        return rows;
    }

    $: weeks = buildWeeks(buildCells(viewYear, viewMonth, value));

    function place() {
        if (!fieldEl) return;
        const r = fieldEl.getBoundingClientRect();
        const left = Math.max(8, Math.min(r.left, window.innerWidth - POPUP_WIDTH - 8));
        let top = r.bottom + 4;
        if (top + POPUP_HEIGHT > window.innerHeight && r.top - POPUP_HEIGHT - 4 > 0) {
            top = r.top - POPUP_HEIGHT - 4;
        }
        popStyle = `left:${left}px;top:${top}px;width:${POPUP_WIDTH}px;`;
    }

    function toggle() {
        if (open) {
            open = false;
            return;
        }
        const p = parse(value);
        const base = p ? new Date(p.y, p.m - 1, 1) : new Date();
        viewYear = base.getFullYear();
        viewMonth = base.getMonth();
        view = "day";
        place();
        open = true;
    }

    function close() {
        open = false;
    }

    function moveMonth(delta) {
        const dt = new Date(viewYear, viewMonth + delta, 1);
        viewYear = dt.getFullYear();
        viewMonth = dt.getMonth();
    }

    function pick(cell) {
        if (cell.disabled) return;
        dispatch("change", cell.str);
        close();
    }

    function pickMonth(m) {
        viewMonth = m;
        view = "day";
    }

    function pickToday() {
        const t = todayStr();
        if (isOutOfRange(t)) return;
        dispatch("change", t);
        close();
    }

    function onWindowMouseDown(e) {
        if (open && root && !root.contains(e.target)) close();
    }

    function onWindowKeydown(e) {
        if (open && e.key === "Escape") close();
    }

    function onWindowScroll(e) {
        // 팝업 위치가 어긋나지 않도록 페이지/컨테이너가 스크롤되면 닫음
        if (open && root && !root.contains(e.target)) close();
    }
</script>

<svelte:window
    on:mousedown={onWindowMouseDown}
    on:keydown={onWindowKeydown}
    on:resize={close}
    on:scroll|capture={onWindowScroll}
/>

<div class="dp-root" bind:this={root}>
    <div class="dp-field" class:compact class:open class:invalid bind:this={fieldEl}>
        <input
            bind:this={inputEl}
            class="dp-input"
            type="text"
            inputmode="numeric"
            autocomplete="off"
            spellcheck="false"
            value={text}
            on:beforeinput={onBeforeInput}
            on:keydown={onKeydown}
            on:paste={onPaste}
            on:mousedown={() => (mouseFocus = true)}
            on:focus={onFocus}
            on:click={onClick}
            on:blur={onBlur}
        />
        <button type="button" class="dp-icon-btn" title="달력 열기" on:click={toggle}>
            <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
                <rect x="1.5" y="3" width="13" height="11.5" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.3" />
                <path d="M1.5 6.5h13M5 1.5v3M11 1.5v3" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" />
            </svg>
        </button>
    </div>

    {#if open}
        <div class="dp-popup" style={popStyle}>
            <div class="dp-head">
                <button type="button" class="dp-title" on:click={() => (view = view === "day" ? "month" : "day")}>
                    {#if view === "day"}
                        {viewYear}년 {viewMonth + 1}월 <span class="caret">▾</span>
                    {:else}
                        {viewYear}년 <span class="caret">▴</span>
                    {/if}
                </button>
                <div class="dp-nav">
                    <button type="button" class="nav-btn" title={view === "day" ? "이전 달" : "이전 해"}
                        on:click={() => (view === "day" ? moveMonth(-1) : (viewYear -= 1))}>↑</button>
                    <button type="button" class="nav-btn" title={view === "day" ? "다음 달" : "다음 해"}
                        on:click={() => (view === "day" ? moveMonth(1) : (viewYear += 1))}>↓</button>
                </div>
            </div>

            {#if view === "day"}
                <div class="dp-grid dp-weekdays">
                    <span class="dp-wd dp-wk-head" title="주차">W</span>
                    {#each WEEKDAYS as w}
                        <span class="dp-wd">{w}</span>
                    {/each}
                </div>
                <div class="dp-grid">
                    {#each weeks as row (row.days[0].str)}
                        <span class="dp-wk" title="{row.week}주차">{row.week}</span>
                        {#each row.days as c (c.str)}
                            <button
                                type="button"
                                class="dp-day"
                                class:out={!c.inMonth}
                                class:today={c.isToday}
                                class:selected={c.isSelected}
                                disabled={c.disabled}
                                on:click={() => pick(c)}
                            >{c.day}</button>
                        {/each}
                    {/each}
                </div>
                <div class="dp-foot">
                    <button type="button" class="today-btn" disabled={isOutOfRange(todayStr())} on:click={pickToday}>오늘</button>
                </div>
            {:else}
                <div class="dp-months">
                    {#each Array(12) as _, i}
                        <button
                            type="button"
                            class="dp-month"
                            class:selected={i === viewMonth}
                            on:click={() => pickMonth(i)}
                        >{i + 1}월</button>
                    {/each}
                </div>
            {/if}
        </div>
    {/if}
</div>

<style>
    .dp-root {
        position: relative;
        width: 100%;
        min-width: 0;
    }
    .dp-field {
        display: flex;
        align-items: center;
        width: 100%;
        height: 35px;
        border: 1.5px solid #cbd5e1;
        border-radius: 4px;
        background: #ffffff;
        box-sizing: border-box;
        transition: all 0.15s ease;
    }
    .dp-field:hover {
        border-color: #94a3b8;
    }
    .dp-field:focus-within,
    .dp-field.open {
        border-color: #3b82f6;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
    }
    .dp-field.invalid {
        border-color: #ef4444;
        box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.12);
    }
    .dp-input {
        flex: 1;
        min-width: 0;
        height: 100%;
        border: none !important;
        outline: none;
        background: transparent;
        padding: 0 4px 0 10px;
        font-size: 0.85rem;
        font-family: inherit;
        font-weight: 600;
        color: #1e293b;
        box-shadow: none !important;
        box-sizing: border-box;
        caret-color: transparent; /* 구간 전체가 선택되는 방식이라 깜빡이는 커서는 숨김 */
    }
    .dp-input::selection {
        background: #2563eb;
        color: #ffffff;
    }
    .compact .dp-input {
        padding-left: 6px;
        font-size: 0.8rem;
    }
    .dp-icon-btn {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 30px;
        height: 100%;
        border: none;
        background: transparent;
        color: #64748b;
        cursor: pointer;
        border-radius: 0 3px 3px 0;
    }
    .dp-icon-btn:hover {
        color: #2563eb;
        background: #f1f5f9;
    }

    .dp-popup {
        position: fixed;
        z-index: 3000;
        background: #ffffff;
        border: 1px solid #94a3b8;
        border-radius: 6px;
        box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.25);
        padding: 10px 12px 8px;
        box-sizing: border-box;
        user-select: none;
    }
    .dp-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 8px;
    }
    .dp-title {
        background: transparent;
        border: none;
        font-size: 0.95rem;
        font-weight: 800;
        color: #0f172a;
        cursor: pointer;
        padding: 2px 4px;
        border-radius: 4px;
    }
    .dp-title:hover {
        background: #f1f5f9;
    }
    .caret {
        font-size: 0.7rem;
    }
    .dp-nav {
        display: flex;
        gap: 4px;
    }
    .nav-btn {
        width: 28px;
        height: 28px;
        border: none;
        background: transparent;
        border-radius: 4px;
        font-size: 1rem;
        color: #334155;
        cursor: pointer;
    }
    .nav-btn:hover {
        background: #e2e8f0;
    }

    .dp-grid {
        display: grid;
        grid-template-columns: 28px repeat(7, 1fr);
        gap: 2px;
    }
    .dp-wk,
    .dp-wk-head {
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.72rem;
        font-weight: 700;
        color: #94a3b8;
        border-right: 1px solid #e2e8f0;
        margin-right: 2px;
    }
    .dp-wk-head {
        display: block;
        text-align: center;
        font-weight: 800;
        color: #94a3b8;
    }
    .dp-weekdays {
        margin-bottom: 2px;
    }
    .dp-wd {
        text-align: center;
        font-size: 0.75rem;
        font-weight: 800;
        color: #475569;
        padding: 4px 0;
    }
    .dp-day {
        height: 32px;
        border: 1px solid transparent;
        background: transparent;
        border-radius: 4px;
        font-size: 0.82rem;
        font-weight: 600;
        color: #1e293b;
        cursor: pointer;
    }
    .dp-day:hover:not(:disabled):not(.selected) {
        background: #e2e8f0;
    }
    .dp-day.out {
        color: #cbd5e1;
    }
    .dp-day.today {
        border-color: #2563eb;
    }
    .dp-day.selected {
        background: #2563eb;
        color: #ffffff;
    }
    .dp-day:disabled {
        color: #e2e8f0;
        cursor: not-allowed;
    }

    .dp-months {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 6px;
        padding: 4px 0 8px;
    }
    .dp-month {
        height: 40px;
        border: 1px solid #e2e8f0;
        background: #ffffff;
        border-radius: 4px;
        font-size: 0.85rem;
        font-weight: 700;
        color: #334155;
        cursor: pointer;
    }
    .dp-month:hover:not(.selected) {
        background: #f1f5f9;
    }
    .dp-month.selected {
        background: #2563eb;
        border-color: #2563eb;
        color: #ffffff;
    }

    .dp-foot {
        display: flex;
        justify-content: flex-end;
        margin-top: 6px;
    }
    .today-btn {
        background: transparent;
        border: none;
        font-size: 0.75rem;
        font-weight: 700;
        color: #2563eb;
        cursor: pointer;
        padding: 2px 6px;
    }
    .today-btn:hover:not(:disabled) {
        text-decoration: underline;
    }
    .today-btn:disabled {
        color: #cbd5e1;
        cursor: not-allowed;
    }
</style>
